import { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import StepNav, { IPPV_STEPS } from "@/components/StepNav";
import { IppvCircuit } from "@/components/ippv/IppvCircuit";
import { PhaseStepper, MachineDebrief } from "@/components/ippv/MachinePanels";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label as FieldLabel } from "@/components/ui/label";
import { Wind, AlertTriangle, ChevronLeft, RotateCcw, Zap, ArrowRight, CheckCircle2, User } from "lucide-react";
import { toast } from "sonner";

/* IPPV & Manual Ventilation — the machine is already set up and leak-tested
   (see /anaes-machine for the setup competency). This simulator drills the
   ventilation itself across three back-to-back clinical rounds so learners
   see how PIP, tidal volume and rate change with the patient. */

// Three clinical rounds — order fixed so the difficulty ramps.
const ROUNDS = [
  {
    id: "cat",
    patient: "Cat — desex, 4.2 kg",
    weightKg: 4.2,
    breaths: 3,
    tvMlPerKg: 12,
    rrTarget: 12,
    pipMax: 15,
    note: "Small patient, compliant lungs. Aim for gentle chest rise around 12 cmH₂O.",
  },
  {
    id: "dog",
    patient: "Dog — laparotomy, 25 kg",
    weightKg: 25,
    breaths: 4,
    tvMlPerKg: 12,
    rrTarget: 10,
    pipMax: 20,
    note: "Bigger tidal volume needed. Peak inspiratory pressure ceiling: 20 cmH₂O.",
  },
  {
    id: "apnoea",
    patient: "Post-induction apnoea — Boxer, 30 kg",
    weightKg: 30,
    breaths: 5,
    tvMlPerKg: 10,
    rrTarget: 12,
    pipMax: 20,
    note: "Patient not breathing after propofol. Deliver rescue breaths until spontaneous ventilation resumes.",
  },
];

const round0 = () => ({
  vaporiserOff: false,
  popOffClosed: false,
  breathThisCycle: false,
  breathCount: 0,
  pressure: 0,
  vaporiserBackOn: false,
  status: "idle",
});

export default function IPPV() {
  const navigate = useNavigate();

  const [roundIdx, setRoundIdx] = useState(0);           // 0..ROUNDS.length-1
  const [phase, setPhase] = useState("round-1");          // "round-1" | "round-2" | "round-3" | "done"
  const [state, setState] = useState(round0);
  const [challenge, setChallenge] = useState(false);
  const [mistakes, setMistakes] = useState([]);
  const [result, setResult] = useState(null);
  const [roundScores, setRoundScores] = useState([]);      // [{roundId, score, max, mistakes:[...]}]
  const mistakeRef = useRef([]);
  const roundMistakeRef = useRef([]);

  const round = ROUNDS[roundIdx];
  const breathsDone = state.breathCount >= round.breaths;
  const tidalMl = Math.round(round.weightKg * round.tvMlPerKg);

  const fail = (msg) => {
    const m = { round: round.id, msg };
    mistakeRef.current = [...mistakeRef.current, m];
    roundMistakeRef.current = [...roundMistakeRef.current, m];
    setMistakes(mistakeRef.current);
    toast.error(msg);
  };

  const updateState = (patch) => setState((s) => ({ ...s, ...patch }));

  /* ── IPPV actions ── */
  const onVapOff = () => updateState({ vaporiserOff: true }) || toast.success("Vaporiser OFF for IPPV — prevents over-deepening");
  const onPopOff = () => {
    if (!state.popOffClosed) {
      if (!state.vaporiserOff) return fail("Turn the vaporiser OFF before IPPV breaths");
      if (breathsDone) return toast.message(`${round.breaths} breaths delivered — advance the round`);
      updateState({ popOffClosed: true, status: "idle" });
      toast.message(`Pop-off CLOSED — ready for breath ${state.breathCount + 1} of ${round.breaths}`);
    } else {
      const wasThisCycle = state.breathThisCycle;
      const nextCount = wasThisCycle ? state.breathCount + 1 : state.breathCount;
      updateState({
        popOffClosed: false, pressure: 0, status: "idle",
        breathThisCycle: false,
        breathCount: nextCount,
      });
      if (wasThisCycle) toast.success(`Breath ${nextCount} of ${round.breaths} delivered — pop-off OPEN`);
      else toast.message("Pop-off valve opened");
    }
  };
  const onSqueeze = () => {
    if (!state.vaporiserOff) return fail("Turn the vaporiser OFF before IPPV breaths");
    if (breathsDone) return toast.message(`${round.breaths} breaths delivered — advance the round`);
    if (!state.popOffClosed) return fail("Breath escapes! Close the pop-off valve before squeezing");

    // Challenge mode OR the round always uses pressure-realism model
    if (challenge) {
      const stepUp = 9 + Math.floor(Math.random() * 5);
      const newP = (state.breathThisCycle ? state.pressure : 0) + stepUp;
      updateState({ breathThisCycle: true, pressure: newP, status: newP > round.pipMax ? "overpressure" : "idle" });
      if (newP > round.pipMax) {
        return fail(`Over-pressure — ${newP} cmH₂O exceeds this patient's ${round.pipMax} cmH₂O ceiling. Ease off the bag.`);
      }
      toast.success(`Squeezing — ${newP} cmH₂O${newP < 10 ? " (shallow)" : " · good chest rise"}`);
      return;
    }

    if (state.breathThisCycle) {
      updateState({ pressure: 45, status: "overpressure" });
      return fail("OVER-PRESSURE! Open the pop-off valve after each breath");
    }
    // Standard mode: safe breath at target PIP
    const targetP = Math.max(12, round.pipMax - 2);
    updateState({ breathThisCycle: true, pressure: targetP });
    toast.success(`Breath ${state.breathCount + 1} of ${round.breaths} — chest rising (≈${targetP} cmH₂O)`);
  };
  const onVapOn = () => {
    if (state.vaporiserBackOn) return;
    if (!breathsDone) return fail("Deliver all rescue breaths before turning the vaporiser back on");
    updateState({ vaporiserBackOn: true });
    toast.success("Vaporiser back ON — maintenance anaesthesia resumed");
    advanceRound();
  };

  const advanceRound = () => {
    // Score this round — distribute the /100 total across rounds so the phase
    // maxes shown in the debrief add up to exactly 100. (The last round takes
    // the remainder when 100 doesn't divide evenly.)
    const perRound = Math.floor(100 / ROUNDS.length);
    const isLastRound = roundIdx === ROUNDS.length - 1;
    const maxPerRound = isLastRound ? 100 - perRound * (ROUNDS.length - 1) : perRound;
    const roundMistakes = roundMistakeRef.current.length;
    const score = Math.max(0, maxPerRound - roundMistakes * 8);
    const rec = { roundId: round.id, patient: round.patient, score, max: maxPerRound, mistakes: [...roundMistakeRef.current] };
    const nextScores = [...roundScores, rec];
    setRoundScores(nextScores);
    roundMistakeRef.current = [];

    if (roundIdx + 1 < ROUNDS.length) {
      const nextIdx = roundIdx + 1;
      setRoundIdx(nextIdx);
      setPhase(`round-${nextIdx + 1}`);
      setState(round0);
      toast.message(`Round ${nextIdx + 1} of ${ROUNDS.length} — ${ROUNDS[nextIdx].patient}`);
    } else {
      finishAll(nextScores);
    }
  };

  const finishAll = (finalScores) => {
    const total = finalScores.reduce((n, r) => n + r.score, 0);
    // Since per-round maxes now sum to exactly 100, no scaling is needed.
    const grade = total >= 90 ? "A" : total >= 80 ? "B" : total >= 70 ? "C" : total >= 60 ? "D" : "F";
    setResult({
      total, grade, mistakes: mistakeRef.current,
      phases: finalScores.map((r) => ({ key: r.roundId, label: r.patient, score: r.score, max: r.max })),
    });
    setPhase("done");
  };

  const reset = () => {
    mistakeRef.current = [];
    roundMistakeRef.current = [];
    setRoundIdx(0); setPhase("round-1"); setState(round0);
    setMistakes([]); setResult(null); setRoundScores([]);
  };

  const setChallengeMode = (on) => {
    setChallenge(on); reset(); setChallenge(on);
    toast.message(on
      ? "Challenge mode ON — each squeeze builds real airway pressure. Watch the PIP ceiling on every round."
      : "Challenge mode OFF — every safe breath delivers at the target PIP for the patient");
  };

  /* ── diagram click dispatchers ── */
  const onVaporiserClick = () => {
    if (!state.vaporiserOff) return onVapOff();
    if (breathsDone && !state.vaporiserBackOn) return onVapOn();
    if (breathsDone) return toast.message("Vaporiser already resumed");
    return toast.message("Vaporiser already OFF — deliver the breaths");
  };
  const onBagClick = () => onSqueeze();
  const onPopoffClick = () => onPopOff();
  const onPatientClick = () => toast.message("Patient is ready — ventilate via the bag");

  /* ── guided "next action" ── */
  const next = (() => {
    if (phase === "done") return null;
    if (!state.vaporiserOff) return { node: "vaporiser", hint: "Turn the vaporiser OFF before IPPV" };
    if (!breathsDone) {
      if (!state.popOffClosed) return { node: "popoff", hint: `Close the pop-off (breath ${state.breathCount + 1}/${round.breaths})` };
      if (!state.breathThisCycle) return { node: "bag", hint: `Squeeze the bag for one breath (target ≤ ${round.pipMax} cmH₂O)` };
      return { node: "popoff", hint: "Re-open the pop-off valve" };
    }
    if (!state.vaporiserBackOn) return { node: "vaporiser", hint: "Turn the vaporiser back ON" };
    return null;
  })();

  const displayPressure = state.pressure;
  const lungsInflated = state.breathThisCycle && state.popOffClosed;
  const bagOverfilled = false;
  const ang = (-120 + (Math.min(displayPressure, 60) / 60) * 240) * (Math.PI / 180);
  const nx = 60 + 38 * Math.cos(ang - Math.PI / 2);
  const ny = 60 + 38 * Math.sin(ang - Math.PI / 2);

  return (
    <div className="min-h-screen bg-[#F4F7F9]">
      <StepNav current={roundIdx + 1} steps={IPPV_STEPS} title="IPPV & Manual Ventilation" subtitle="Three patient rounds · safe manual breaths" />
      <main className="max-w-6xl mx-auto px-6 py-8 fade-up">
        <header className="mb-6">
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-[#0047AB] font-semibold mb-2">
            <Wind className="w-3.5 h-3.5" />
            <span>IPPV · manual ventilation</span>
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold tracking-tight text-slate-900">IPPV &amp; Manual Ventilation</h1>
          <p className="text-base text-slate-600 mt-2 max-w-2xl">
            The machine is set up and leak-tested. Deliver safe manual breaths across three back-to-back patient rounds —
            small, large and apnoeic. The golden rule for IPPV:{" "}
            <span className="font-semibold text-slate-900">always re-open the pop-off valve after every breath.</span>
            {" "}Prefer the full workflow? Complete the <span className="font-semibold text-slate-900">Anaesthetic Machine Setup</span> first.
          </p>
        </header>

        <div className="mb-6">
          <PhaseStepper
            phase={phase}
            phases={ROUNDS.map((r, i) => ({ key: `round-${i + 1}`, label: `Round ${i + 1} · ${r.id}` }))}
            order={[...ROUNDS.map((_, i) => `round-${i + 1}`), "done"]}
          />
        </div>

        {phase === "done" ? (
          <div className="space-y-5">
            <MachineDebrief
              result={result}
              onReset={reset}
              simulatorId="ippv"
              simulatorName="IPPV & Manual Ventilation"
              gradeContext="Cert IV manual ventilation"
            />
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" onClick={() => navigate("/sections")} data-testid="back-to-home-btn" className="text-slate-600">
                <ChevronLeft className="w-4 h-4 mr-1" /> Back to Simulator Hub
              </Button>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start">
            <div className="space-y-4">
              <RoundCard round={round} roundIdx={roundIdx} totalRounds={ROUNDS.length} tidalMl={tidalMl} />
              <HintCard next={next} />
              <ChallengeCard challenge={challenge} setChallengeMode={setChallengeMode} />

              {state.status === "overpressure" && (
                <div className="flex items-center gap-2 bg-red-50 border border-red-300 rounded-md px-3 py-2.5 text-red-700 text-xs font-semibold alarm-pulse" data-testid="overpressure-alarm">
                  <AlertTriangle className="w-4 h-4" />
                  Over-pressure — open the pop-off valve immediately to release.
                </div>
              )}

              {mistakes.length > 0 && (
                <div className="text-[11px] text-slate-500" data-testid="live-mistakes">
                  {mistakes.length} note{mistakes.length === 1 ? "" : "s"} logged this run
                </div>
              )}

              <div className="flex flex-wrap gap-2">
                <Button onClick={reset} data-testid="ippv-reset-btn" className="bg-[#0047AB] hover:bg-[#003380] text-white flex-1 sm:flex-none">
                  <RotateCcw className="w-4 h-4 mr-1.5" /> Reset
                </Button>
                <Button variant="outline" onClick={() => navigate("/sections")} data-testid="back-to-home-btn" className="text-slate-600 flex-1 sm:flex-none">
                  <ChevronLeft className="w-4 h-4 mr-1" /> Back to Hub
                </Button>
              </div>
            </div>

            <div className="lg:col-span-2">
              <IppvCircuit
                phase="ippv"                       /* keep the diagram in its IPPV visual state */
                next={next} onFixLeak={() => {}}
                gasFlowing={true} o2On={true} spareChecked={true} flow={2}
                vapFilled={true} vaporiserPct={2} vaporiserOff={state.vaporiserOff} vaporiserBackOn={state.vaporiserBackOn}
                sodaLimeChecked={true} circuitChecked={true} scavengingOn={true}
                preuseSigned={true} setupCoreReady={true}
                aplClosed={false} occluded={false} leaking={false} popClosed={state.popOffClosed}
                breathsDone={breathsDone} breathCount={state.breathCount}
                displayPressure={displayPressure} nx={nx} ny={ny} lungsInflated={lungsInflated} bagOverfilled={bagOverfilled}
                onO2={() => {}} onSpare={() => {}} onFlow={() => {}} onVaporiser={onVaporiserClick} onSodaLime={() => {}}
                onBag={onBagClick} onPopoff={onPopoffClick} onPatient={onPatientClick} onScavenging={() => {}} onClipboard={() => {}}
              />
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

/* ── round-specific patient briefing ────────────────────────────── */
const RoundCard = ({ round, roundIdx, totalRounds, tidalMl }) => (
  <Card className="border-slate-200" data-testid={`ippv-round-card-${round.id}`}>
    <CardHeader className="pb-3">
      <CardTitle className="text-sm font-semibold text-slate-900 uppercase tracking-wider flex items-center gap-2">
        <User className="w-4 h-4 text-[#0047AB]" />
        Round {roundIdx + 1} of {totalRounds}
      </CardTitle>
    </CardHeader>
    <CardContent className="space-y-3 text-sm">
      <div className="text-slate-900 font-semibold" data-testid="ippv-round-patient">{round.patient}</div>
      <div className="text-slate-600 leading-relaxed">{round.note}</div>
      <div className="grid grid-cols-3 gap-2 text-center">
        <MiniStat label="Breaths" value={round.breaths} />
        <MiniStat label="Target PIP" value={`≤${round.pipMax}`} unit="cmH₂O" />
        <MiniStat label="Tidal vol" value={tidalMl} unit="ml" />
      </div>
    </CardContent>
  </Card>
);

const MiniStat = ({ label, value, unit }) => (
  <div className="rounded-md border border-slate-200 bg-slate-50 px-2 py-1.5">
    <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{label}</div>
    <div className="text-lg font-bold text-slate-900 leading-tight">{value}</div>
    {unit && <div className="text-[10px] text-slate-500">{unit}</div>}
  </div>
);

const HintCard = ({ next }) => (
  <Card className="border-[#0047AB]/30 bg-[#0047AB]/[0.03]" data-testid="ippv-hint-card">
    <CardContent className="pt-5">
      <div className="text-[11px] uppercase tracking-[0.18em] text-[#0047AB] font-semibold mb-1.5">Next action</div>
      {next ? (
        <div className="flex items-start gap-2.5">
          <span className="mt-0.5 w-6 h-6 rounded-full bg-[#0047AB] text-white flex items-center justify-center shrink-0 animate-pulse">
            <ArrowRight className="w-3.5 h-3.5" />
          </span>
          <p className="text-sm text-slate-800 font-medium leading-snug" data-testid="ippv-next-hint">{next.hint}</p>
        </div>
      ) : (
        <p className="text-sm text-emerald-700 font-medium flex items-center gap-1.5" data-testid="ippv-next-hint">
          <CheckCircle2 className="w-4 h-4" /> Round complete — advancing.
        </p>
      )}
      <p className="text-[11px] text-slate-500 mt-2.5 leading-relaxed">Operate the machine by clicking the equipment in the diagram →</p>
    </CardContent>
  </Card>
);

const ChallengeCard = ({ challenge, setChallengeMode }) => (
  <Card className={`border-2 transition-colors ${challenge ? "border-amber-300 bg-amber-50/40" : "border-slate-200"}`} data-testid="challenge-card">
    <CardContent className="pt-5 flex items-start gap-3">
      <div className={`w-9 h-9 rounded-md flex items-center justify-center shrink-0 ${challenge ? "bg-amber-500" : "bg-slate-200"}`}>
        <Zap className={`w-4 h-4 ${challenge ? "text-white" : "text-slate-500"}`} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2">
          <FieldLabel htmlFor="challenge-toggle" className="text-sm font-semibold text-slate-900 cursor-pointer">Challenge mode</FieldLabel>
          <Switch id="challenge-toggle" checked={challenge} onCheckedChange={setChallengeMode} data-testid="challenge-toggle" />
        </div>
        <p className="text-[11px] text-slate-500 leading-relaxed mt-1">
          Every squeeze now builds real airway pressure. Watch the PIP ceiling — each round has a different limit that matches the patient.
        </p>
      </div>
    </CardContent>
  </Card>
);
